import * as React from "react";
import { Block } from "baseui/block";
import { Button as BaseButton, SHAPE, SIZE } from "baseui/button";
import { ButtonGroup, MODE as BASE_MODE } from "baseui/button-group";
import { getOverrides, mergeOverrides } from "baseui/helpers/overrides";
import { Image } from "../Image";
import { LabelMedium, ParagraphXSmall } from "../typography";
import { maybe } from "kanopi-components/utils/functional";
import { useStyletron } from "kanopi-components/theme/useStyletron";

/* eslint-disable @typescript-eslint/ban-types */
// TODO: Enable the proptype check next time when this file is changed

/**
 * Base Button style input - should not be used directly
 * If you want the user to select only one option, use ButtonRadioInput
 * If you want the user to select multiple options, use ButtonCheckboxInput
 */
export const ButtonInput = ({
  onChange,
  onBlur,
  value,
  options,
  mode,
  size,
  overrides = {},
  textCenter = false,
  fullWidthForDescription = true,
  ...restProps
}: ButtonInputProps) => {
  const [, theme] = useStyletron();
  const { Button: buttonOverrides } = overrides;
  const [Button, buttonProps] = getOverrides(buttonOverrides, BaseButton);

  const optionsState = {};
  options.forEach((option, index) => {
    optionsState[index] = value.map(String).indexOf(option.value.toString()) !== -1;
  });
  const selectedOptions = Object.keys(optionsState)
    .filter((key) => optionsState[key])
    .map((v) => parseInt(v, 10));
  const hasDetail = options.some((option) => option.description || option.icon);

  return (
    <ButtonGroup
      mode={BASE_MODE[mode]}
      selected={selectedOptions}
      size={size}
      shape={size === SIZE.mini ? SHAPE.pill : SHAPE.default}
      onClick={(event, index) => {
        event.preventDefault();
        if (mode === "radio") {
          Object.keys(options).forEach((key) => {
            optionsState[key] = key === index.toString();
          });
        } else {
          optionsState[index] = !optionsState[index];
        }
        const selectedValues = options
          .filter((_, i) => optionsState[i])
          .map(({ value }) => value);
        onChange(selectedValues);
        // onBlur and value are changed in the same event loop. This will cause onBluer
        // to be called with the old value. This is a workaround to make sure onBlur is
        // called with the new value.
        setTimeout(() => {
          maybe(onBlur)();
        }, 0);
      }}
      overrides={{
        Root: {
          style: {
            width: "100%",
            justifyContent: "center",
            flexWrap: "wrap",
          },
        },
      }}
      {...restProps}
    >
      {options.map((option, index) => {
        const selected = selectedOptions.indexOf(index) !== -1;
        return (
          <Button
            {...buttonProps}
            overrides={mergeOverrides(
              {
                BaseButton: {
                  style: ({ $theme }) =>
                    getButtonStyle({
                      theme: $theme,
                      selected,
                      size,
                      hasDetail,
                      fullWidthForDescription,
                    }),
                  props: {
                    "aria-checked": selected,
                    role: mode,
                  },
                },
              },
              buttonProps.overrides
            )}
            key={index}
          >
            {option.icon && (
              <Image
                src={option.icon}
                width="50px"
                height="50px"
                flex="0 0 auto"
                marginBottom={0}
                marginRight="scale800"
                strictRatio
              />
            )}
            {hasDetail ? (
              <Block
                display="flex"
                flexDirection="column"
                flex={1}
                alignItems={textCenter ? "center" : "flex-start"}
              >
                <LabelMedium
                  color={
                    selected
                      ? theme.colors.contentInversePrimary
                      : theme.colors.contentPrimary
                  }
                >
                  {option.label}
                </LabelMedium>
                <ParagraphXSmall
                  marginTop={0}
                  marginBottom={0}
                  color={
                    selected
                      ? theme.colors.contentInversePrimary
                      : theme.colors.contentPrimary
                  }
                >
                  {option.description}
                </ParagraphXSmall>
              </Block>
            ) : (
              option.label
            )}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

export function getButtonStyle({
  theme,
  selected,
  size,
  hasDetail,
  fullWidthForDescription = true,
}) {
  const defaultColour =
    size === SIZE.mini ? "transparent" : theme.colors.buttonSecondaryFill;
  const highlightColor =
    size === SIZE.mini ? theme.colors.primary500 : theme.colors.primary;
  const hoverColor =
    size === SIZE.mini ? theme.colors.primary100 : theme.colors.buttonSecondaryHover;
  const fontColor =
    selected && highlightColor === theme.colors.primary
      ? theme.colors.contentInversePrimary
      : theme.colors.contentPrimary;

  return {
    marginBottom: theme.sizing.scale500,
    color: fontColor,
    textTransform: hasDetail ? null : "uppercase",
    fontSize: "14px",
    fontWeight: 600,
    backgroundColor: selected ? highlightColor : defaultColour,
    flexBasis: "100%",
    justifyContent: hasDetail ? null : "center",
    ...(size === SIZE.mini && {
      marginLeft: "10px",
      marginRight: "10px",
      flexBasis: "0%",
      fontWeight: 400,
    }),
    ...(size !== SIZE.mini &&
      (!hasDetail || !fullWidthForDescription) && {
        [`@media screen and (min-width: ${theme.breakpoints.medium}px)`]: {
          flexBasis: "180px",
          marginLeft: "10px",
          marginRight: "10px",
        },
      }),
    ":hover": {
      backgroundColor: selected ? highlightColor : hoverColor,
    },
  };
}

ButtonInput.defaultProps = {
  onChange: () => undefined,
};

export type Value = string | number | boolean;

export type Option = {
  value: string | number | boolean;
  label: string;
  description?: string;
  icon?: string;
};

type ButtonInputProps = {
  onChange: Function;
  onBlur?: (...args: unknown[]) => void;
  value: Array<Value>;
  options: Array<Option>;
  size?: "compact" | "default" | "large" | "mini";
  mode: "radio" | "checkbox";
  overrides?: object;
  textCenter?: boolean;
  fullWidthForDescription?: boolean;
};
